'use client';

import { createContext, useContext } from 'react';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { browserNavigator } from '../lib/browserSurface';
import { LANGUAGE_CODES, LANG_COOKIE, LANG_COOKIE_MAX_AGE, languageFromCode } from '../i18n/locale';

export const languageCodes = LANGUAGE_CODES;

export type Language = (typeof languageCodes)[number];

export const languages: Record<Language, string> = {
  en: 'English',
  ru: 'Русский',
  uz: "O'zbekcha",
  fa: 'فارسی',
  pt: 'Português',
  tr: 'Türkçe',
};

import { LANGUAGE_DIR } from '../i18n/types';

export { LANGUAGE_DIR };

function getLanguageFromTelegramCode(languageCode?: string): Language {
  return languageFromCode(languageCode) ?? 'en';
}

function getLanguageFromInitData(): Language | null {
  if (typeof window === 'undefined') return null;
  try {
    const urlParams = new URLSearchParams(window.location.hash.slice(1));
    const initData = urlParams.get('tgWebAppData');

    if (initData) {
      const params = new URLSearchParams(initData);
      const userParam = params.get('user');

      if (userParam) {
        const user = JSON.parse(decodeURIComponent(userParam));
        return getLanguageFromTelegramCode(user.language_code);
      }
    }
  } catch (error) {
    console.error('Failed to parse initData for language:', error);
  }

  return null;
}

function getLanguageFromBrowser(): Language | null {
  if (typeof navigator === 'undefined') return null;
  try {
    const langs = browserNavigator().languages ?? [navigator.language];
    for (const lang of langs) {
      const language = languageFromCode(lang);
      if (language) return language;
    }
  } catch {
  }
  return null;
}

function getLanguageFromCookie(): Language | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${LANG_COOKIE}=(ru|en|uz|fa|pt|tr)(?:;|$)`),
  );
  return languageCodes.find((code) => code === match?.[1]) ?? null;
}

function writeLanguageCookie(language: Language): void {
  if (typeof document === 'undefined') return;
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:'
    ? '; Secure'
    : '';
  document.cookie = `${LANG_COOKIE}=${language}; path=/; max-age=${LANG_COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
}

// WHY: профиль отдаёт сырой код Telegram (`pt-br`) — сравнение целой строкой отправит бразильцев в английский; только
// субтег
export const clampLanguage = (raw: string | undefined): Language =>
  languageFromCode(raw) ?? 'en';

export function applyDocumentLang(language: Language): void {
  if (typeof document === 'undefined') return;
  const clamped = clampLanguage(language);
  document.documentElement.lang = clamped;
  document.documentElement.dir = LANGUAGE_DIR[clamped];
}

interface LanguageState {
  language: Language;
  setLanguage: (language: Language) => void;
}

// WHY: на SSR и первой гидрации язык всегда 'ru', иначе hydration mismatch; настоящий ставится в useEffect
export const useLanguageStore = create<LanguageState>()(
  devtools(
    (set) => ({
      language: 'ru',
      setLanguage: (language) => {
        writeLanguageCookie(language);
        applyDocumentLang(language);
        set({ language }, false, 'setLanguage');
      },
    }),
    { name: 'LanguageStore' }
  )
);

export function detectAndApplyBrowserLanguage(): void {
  const detected =
    getLanguageFromCookie() ??
    getLanguageFromInitData() ??
    getLanguageFromBrowser();
  if (detected) {
    useLanguageStore.getState().setLanguage(detected);
  }
}

// WHY: на вебе язык задаёт адрес и layout отдаёт его контекстом — zustand на сервере видит только начальный 'ru', и
// SSR-разметка выходила русской при любом языке; в Mini App контекста нет, там язык живёт в сторе
export const RequestLanguageContext = createContext<Language | null>(null);

export const useLanguage = (): Language => {
  const fromRequest = useContext(RequestLanguageContext);
  const stored = useLanguageStore((state) => state.language);
  return fromRequest ?? stored;
};
export const useSetLanguage = () => useLanguageStore((state) => state.setLanguage);
